// Balloon (§3.3): FLOAT from an item use (player-steered, up to 1.5 m) and the passive auto-save
// (a held, unused Balloon inflates while its holder falls; it rises to 1.0 m and steers itself
// back to the nearest ground). `escape` = FLOAT_SAVE marks the auto-save (replicated, so the
// local bean predicts it exactly). Every branch also runs for the local bean in predict mode.
import { DT, NO_SLOT, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { BS, ItemKind } from '../ids';
import { MAPS } from '../maps/index';
import { createGroundHit } from '../maps/mapTypes';
import { MODES } from '../modes/index';
import { approachVec, clampLength } from '../math';
import type { Bean, Vec2, World } from '../types';
import { forceFall } from './edges';
import { creditOf } from './world';

/** `Bean.escape` of a FLOAT that is a balloon auto-save. */
export const FLOAT_SAVE = 1;

const RISE_PER_TICK = TUNE.balloon.floatZ / TUNE.balloon.riseTicks;
const hit = createGroundHit();

/**
 * Enters FLOAT (§3.3): a fresh balloon timer, no vertical speed, and the horizontal speed caught
 * down to the balloon's max drift speed (the balloon catches a tumbling or falling bean).
 */
export function startFloat(w: World, b: Bean, save: boolean): void {
  b.state = BS.FLOAT;
  b.st = 0;
  b.floatTicks = save ? TUNE.balloon.saveTicks : TUNE.balloon.floatTicks;
  b.escape = save ? FLOAT_SAVE : 0;
  b.partner = NO_SLOT;
  b.vz = 0;
  clampLength(b.vel, b.vel.x, b.vel.y, TUNE.balloon.maxSpeed);
  emit(w, EV.FLOAT_START, b.slot);
}

/** The balloon is gone (popped by `by`, or NO_SLOT when it ran out or landed). The caller sets the new state. */
export function popFloat(w: World, b: Bean, by: number): void {
  b.floatTicks = 0;
  b.escape = 0;
  emit(w, EV.FLOAT_POP, b.slot, by);
}

/** Leaves FLOAT from its height (§3.3): over ground the bean drops and lands; over void it falls. */
function dropFromFloat(w: World, b: Bean): void {
  b.vz = 0;
  const g = MAPS[w.cfg.mapId].groundAt(w, b.pos.x, b.pos.y);
  if (g === VOID) {
    forceFall(w, b.slot, b.vel.x, b.vel.y, Math.hypot(b.vel.x, b.vel.y));
    return;
  }
  b.state = BS.NORMAL;
  b.st = 0;
  b.groundZ = g;
  if (b.z < g) b.z = g;
}

/** Timer phase: the balloon timer runs out (an auto-save that never reached ground pops). */
export function tickFloat(w: World, b: Bean): void {
  if (b.state !== BS.FLOAT) return;
  if (b.floatTicks > 0) b.floatTicks--;
  if (b.floatTicks > 0) return;
  popFloat(w, b, NO_SLOT);
  dropFromFloat(w, b);
}

/**
 * Movement phase. Item float: rise to 1.5 m over 24 ticks and drift with 10 m/s² of control up to
 * 4 m/s (2 m/s² drag without input). Auto-save: rise to 1.0 m at the same rate and steer at 4 m/s
 * toward a point `saveInset` inside the nearest ground; once the bean is fully over ground the
 * balloon lets go and the bean drops to land.
 */
export function moveFloat(w: World, b: Bean, move: Vec2): void {
  const f = TUNE.balloon;
  if (b.escape === FLOAT_SAVE) {
    const map = MAPS[w.cfg.mapId];
    map.nearestGround(w, b.pos.x, b.pos.y, hit);
    if (map.groundAt(w, b.pos.x, b.pos.y) !== VOID && hit.dist >= b.radius) {
      popFloat(w, b, NO_SLOT);
      dropFromFloat(w, b);
      return;
    }
    const tx = hit.x + hit.nx * f.saveInset - b.pos.x;
    const ty = hit.y + hit.ny * f.saveInset - b.pos.y;
    const l = Math.hypot(tx, ty);
    approachVec(b.vel, l > 0 ? (tx / l) * f.saveSpeed : 0, l > 0 ? (ty / l) * f.saveSpeed : 0, f.accel * DT);
    b.z = Math.min(f.saveZ, b.z + RISE_PER_TICK);
  } else {
    if (Math.hypot(move.x, move.y) >= TUNE.bean.noInputThreshold) approachVec(b.vel, move.x * f.maxSpeed, move.y * f.maxSpeed, f.accel * DT);
    else approachVec(b.vel, 0, 0, f.drag * DT);
    b.z = Math.min(f.floatZ, b.z + RISE_PER_TICK);
  }
  b.vz = 0;
  b.pos.x += b.vel.x * DT;
  b.pos.y += b.vel.y * DT;
}

/**
 * Edge phase, FALLING (§3.3 passive auto-save): a held, unused Balloon inflates once the bean has
 * dropped `saveTriggerDepth` below its last ground (a beat of panic first) while still above
 * `saveMinZ`, unless the mode blocks items. Emits ITEM_USE, FLOAT_START and BALLOON_SAVE (b = the
 * bean whose KO it denied). Returns true when the save fired.
 */
export function checkBalloonSave(w: World, b: Bean): boolean {
  const f = TUNE.balloon;
  if (b.item !== ItemKind.BALLOON || b.charges <= 0) return false;
  if (b.z > b.groundZ - f.saveTriggerDepth || b.z <= f.saveMinZ) return false;
  if (MODES[w.cfg.modeId].itemsBlocked(w, b.slot)) return false;
  if (--b.charges <= 0) {
    b.item = ItemKind.NONE;
    b.charges = 0;
  }
  emit(w, EV.ITEM_USE, b.slot, ItemKind.BALLOON);
  startFloat(w, b, true);
  emit(w, EV.BALLOON_SAVE, b.slot, creditOf(w, b), 0, 0, b.pos.x, b.pos.y);
  return true;
}
